import {
  castTimeBreakdownStrings,
  getTotalSeconds,
} from '@automata/ui/src/DurationPickerV2/utils';
import { TaskOutputReferenceInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { z } from 'zod';

import { emptyStringToHyphenSchema, emptyStringToNullSchema } from './utils';
import { PropertiesType } from '../types';

export type TaskParameterValue =
  | string
  | number
  | boolean
  | string[]
  | TaskOutputReferenceInput;

// TODO(IL, 2025-02-20): since the task type matches the action name in drivers
// and is dynamic, we can't predefine types here. We should probably remove this type
// and just use `string`
export type TaskType = string;

// should this be called ActionTaskProperties?
// It is used in the AllTaskProperties union alongside the other task property types
export type TaskProperties = {
  id: string;
  nickname: string;
  userTimeEstimate: number | null;
  taskType: TaskType;
  instrumentId: string | null;
  taskParameters: Record<string, TaskParameterValue> | null;
  taskAppliedParameters?: Record<string, string> | null;
  propertiesType: PropertiesType.Action;
  labwareIdsToInputSlots?: Record<string, string | number | null>;
  labwareIdsToOutputSlots?: Record<string, string | number | null>;
  labwareIdsToUnitsConsumed: Record<string, number | null>;
  workflowDataMode?: DataMode;
  inputCsvDataMode?: DataMode;
  plateMapDataMode?: DataMode;
  isLabwareLessTask?: boolean;
};

/** TaskProperties fields that are valid to include in a patch */
export type TaskPropertiesPatch = Partial<
  Omit<TaskProperties, 'id' | 'taskType'>
>;

// NOTE(IL, 2024-03-20): string fields represent "no value" using '' instead of null
// to comply with MUI form components (eg Select)
/** This is TaskProperties made more generic for HTML form components, and taskParameters is removed
 * bc they're presented separately */
export type TaskPropertiesForm = {
  id: string;
  nickname: string;
  userTimeEstimateHours: string;
  userTimeEstimateMinutes: string;
  userTimeEstimateSeconds: string;
  taskType: string;
  instrumentId: string;
  workflowDataMode?: DataMode;
  inputCsvDataMode?: DataMode;
  plateMapDataMode?: DataMode;
};

const taskParameterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.string().array(),
]) satisfies z.ZodType<TaskParameterValue>;

const taskPropertiesTimeEstimateFieldsSchema = z.object({
  userTimeEstimateHours: z.string().nullable().optional(),
  userTimeEstimateMinutes: z.string().nullable().optional(),
  userTimeEstimateSeconds: z.string().nullable().optional(),
});

export const _transformTimeEstimates = ({
  userTimeEstimateHours,
  userTimeEstimateMinutes,
  userTimeEstimateSeconds,
}: z.infer<typeof taskPropertiesTimeEstimateFieldsSchema>): Pick<
  TaskProperties,
  'userTimeEstimate'
> => {
  const isBlank = (s: string | null | undefined): boolean =>
    s == null || s.trim() === '';

  if (
    isBlank(userTimeEstimateHours) &&
    isBlank(userTimeEstimateMinutes) &&
    isBlank(userTimeEstimateSeconds)
  ) {
    return { userTimeEstimate: null };
  }

  return {
    userTimeEstimate: getTotalSeconds(
      castTimeBreakdownStrings({
        hours: userTimeEstimateHours ?? '0',
        minutes: userTimeEstimateMinutes ?? '0',
        seconds: userTimeEstimateSeconds ?? '0',
      })
    ),
  };
};

export enum DataMode {
  Manual = 'manual',
  Connector = 'connector',
}

export const workflowDataModeSchema = z.enum([
  DataMode.Manual,
  DataMode.Connector,
]);

export const inputCsvDataModeSchema = z.enum([
  DataMode.Manual,
  DataMode.Connector,
]);

export const plateMapDataModeSchema = z.enum([
  DataMode.Manual,
  DataMode.Connector,
]);

/** Sub-schema of taskProperties, containing fields that are allowed to be updated. */
export const taskPropertiesUpdatableSchema = z.object({
  nickname: emptyStringToHyphenSchema,
  taskType: z.string(),
  instrumentId: emptyStringToNullSchema,
  taskParameters: z.record(z.string(), taskParameterValueSchema).nullable(),
  labwareIdsToInputSlots: z
    .record(z.string(), z.union([z.string(), z.number()]).nullable())
    .optional(),
  labwareIdsToOutputSlots: z
    .record(z.string(), z.union([z.string(), z.number()]).nullable())
    .optional(),
  labwareIdsToUnitsConsumed: z.record(z.string(), z.number().nullable()),
  workflowDataMode: workflowDataModeSchema.optional(),
  inputCsvDataMode: inputCsvDataModeSchema.optional(),
  plateMapDataMode: plateMapDataModeSchema.optional(),
});

export const taskPropertiesPatchSchema = taskPropertiesUpdatableSchema
  .merge(taskPropertiesTimeEstimateFieldsSchema)
  .partial()
  .transform(
    ({
      userTimeEstimateHours,
      userTimeEstimateMinutes,
      userTimeEstimateSeconds,
      workflowDataMode,
      inputCsvDataMode,
      plateMapDataMode,
      ...otherFields
    }) => {
      const hasTimeFields =
        userTimeEstimateHours !== undefined ||
        userTimeEstimateMinutes !== undefined ||
        userTimeEstimateSeconds !== undefined;

      const timeEstimateFields = hasTimeFields
        ? _transformTimeEstimates({
            userTimeEstimateHours,
            userTimeEstimateMinutes,
            userTimeEstimateSeconds,
          })
        : {};
      const workflowDataModeField = workflowDataMode
        ? { workflowDataMode }
        : {};
      const inputCsvDataModeField = inputCsvDataMode
        ? { inputCsvDataMode }
        : {};
      const plateMapDataModeField = plateMapDataMode
        ? { plateMapDataMode }
        : {};

      return {
        ...otherFields,
        ...timeEstimateFields,
        ...workflowDataModeField,
        ...inputCsvDataModeField,
        ...plateMapDataModeField,
      };
    }
  ) satisfies z.ZodType<TaskPropertiesPatch>;
